import { useLayout } from '@capitalise/ui-design-system';
import { useMutationCancelMatch } from 'modules/Business/services/matches/queries';
import {
  useMutationAwaitClientDecision,
  useMutationContinueWithOffer,
  useMutationRejectOffer,
} from 'modules/Business/services/offers/queries';
import { useCallback } from 'react';
import { useQueryGetActivityFeedbackTimeline } from 'services/monitor/queries';
import { options } from 'modules/Business/components/Funding/ProductOptionsDropdown';

const useMatchesActions = (
  fundingSearchId,
  refetchMatches,
  refetchReduxMatches,
  successCallback,
  errorCallback,
  openLenderModal,
) => {
  const { renderAlertDialog } = useLayout();

  const { mutate: cancelMatch } = useMutationCancelMatch();
  const { mutate: rejectOffer } = useMutationRejectOffer();
  const { mutate: awaitClientDecision } = useMutationAwaitClientDecision();
  const { mutate: continueWithOffer } = useMutationContinueWithOffer();

  const { data: timelineData } = useQueryGetActivityFeedbackTimeline({
    normalizedCases: true,
    sort: 'createdAt desc',
  });

  let offerActivity;

  const getOfferActivity = (offerId) =>
    (offerActivity = timelineData?.filter((activity) => activity.offerId === offerId)?.[0]);

  const refetch = () => {
    refetchMatches();
    refetchReduxMatches(fundingSearchId);
  };

  const callRemoveLender = (id, name) => {
    renderAlertDialog({
      key: 'deleteSearch',
      label: 'Remove lender',
      description: `You're about to remove ${name} from your matched lenders. Are you sure?`,
      action: 'Remove lender',
      onConfirmation: () => {
        cancelMatch(id, {
          onSuccess: () => {
            refetch();
            successCallback('Lender successfully removed');
          },
          onError: () => {
            errorCallback('Failed to remove lender');
          },
        });
      },
    });
  };

  const callDeclineOffer = (offerId) => {
    getOfferActivity(offerId);
    rejectOffer(offerActivity.id, {
      onSuccess: () => {
        refetch();
        successCallback('Offer declined successfully');
      },
      onError: () => {
        errorCallback('Failed to declined offer');
      },
    });
  };

  const callAwaitDecision = (offerId) => {
    getOfferActivity(offerId);
    awaitClientDecision(offerActivity.id, {
      onSuccess: () => {
        refetch();
        successCallback('Moved to awaiting client desicion succesfully');
      },
      onError: () => {
        errorCallback('Failed to move awaiting client desicion');
      },
    });
  };

  const callContinueWithOffer = (offerId) => {
    getOfferActivity(offerId);
    continueWithOffer(offerActivity.id, {
      onSuccess: () => {
        refetch();
        successCallback('Continued with offer succesfully');
      },
      onError: () => {
        errorCallback('Failed to continue with offer');
      },
    });
  };

  const onActionClick = useCallback(
    (id, name, offerId) => (selectedOption) => {
      switch (selectedOption) {
        case options.FAILED_LENDER:
          openLenderModal(true);
          break;
        case options.REMOVE_LENDER:
          callRemoveLender(id, name);
          break;
        case options.DECLINE_OFFER:
          callDeclineOffer(offerId);
          break;
        case options.AWAIT_DECISION:
          callAwaitDecision(offerId);
          break;
        case options.CONTINUE_WITH_OFFER:
          callContinueWithOffer(offerId);
          break;
        default:
          break;
      }
    },
    [timelineData],
  );

  return { onActionClick };
};

export default useMatchesActions;
